import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  NgZone,
  Renderer2, ChangeDetectorRef
} from '@angular/core';
import { Video } from '@models';
import { BehaviorSubject, combineLatest, timer, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

interface Dimensions {
  x: number;
  y: number;
  width: number;
  height: number;
}

const emptyRect: Dimensions = { x: 0, y: 0, width: 0, height: 0 };

@Component({
  selector: 'thevatra-zwift-screenshare',
  templateUrl: './zwift-screenshare.component.html',
  styleUrls: ['./zwift-screenshare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZwiftScreenshareComponent implements AfterViewInit, OnDestroy {
  @ViewChild('zwiftVideo') zwiftVideo: ElementRef<HTMLVideoElement>;

  @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;
  private hostWidth$ = new BehaviorSubject<Dimensions | null>(null);
  private videoDimensions$ = new BehaviorSubject<Dimensions>(emptyRect);
  private recognizedArea$ = new BehaviorSubject<Dimensions | null>(null);
  private hostResizeObserver: ResizeObserver;
  private videoResizeObserver: ResizeObserver;

  constructor(
    private hostElementRef: ElementRef,
    private renderer2: Renderer2,
    private ngZone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  @Input() set source(video: Video) {
    this.stream = video.stream;
    this.setMediaStream(this.stream);
  }

  private stream: MediaStream;
  private readonly destroyed$ = new Subject<boolean>();

  ngAfterViewInit() {
    this.setMediaStream(this.stream);
    this.resizingVideoWhenHostElementSizeChanging();
  }

  private setMediaStream(mediaStream: MediaStream): void {
    if (!this.zwiftVideo?.nativeElement) {
      return;
    }

    // attach stream to video
    // copied from Janus demo
    // https://janus.conf.meetecho.com/mvideoroomtest.js
    try {
      this.zwiftVideo.nativeElement.srcObject = mediaStream;
    } catch (e1) {
      try {
        this.zwiftVideo.nativeElement.src = URL.createObjectURL(mediaStream as unknown as MediaSource);
      } catch (e2) {
        console.error('Error attaching stream to element', e1, e2);
      }
    }
  }

  private resizingVideoWhenHostElementSizeChanging(): void {
    const percentageFromVideoHeightWithNeededBlock = 27;
    const expectedPaddingFromNeededBlock = 2.4;

    this.handleHostRefWidthChanges();
    this.handleVideoDimensionsChanges();

    type CombinedVideoData = [Dimensions | null, Dimensions, Dimensions | null];

    combineLatest([
      this.hostWidth$.asObservable(),
      this.videoDimensions$.asObservable(),
      this.recognizedArea$.asObservable(),
    ])
      .pipe(
        takeUntil(this.destroyed$),
        filter(() => !!(this.zwiftVideo?.nativeElement)),
        filter(([host, v, mbArea]: CombinedVideoData) => host !== null && mbArea !== null),
      )
      .subscribe(([mbHost, v, mbArea]: CombinedVideoData) => {
        const area = mbArea || emptyRect; // actually not null as filtered above
        const host = mbHost || emptyRect; // actually not null as filtered above
        // const newVideoWidth = width * 3;
        const zwiftVideoElement = this.zwiftVideo.nativeElement;

        // this.renderer2.setStyle(zwiftVideoElement, 'width', `${newVideoWidth}px`);

        // const zwiftVideoHeight = zwiftVideoElement.getBoundingClientRect().height;
        // const topPaddingFromHeightCurrentVideo = expectedPaddingFromNeededBlock * zwiftVideoHeight / 100;
        // const newHeightOfVideoWrapper = percentageFromVideoHeightWithNeededBlock * zwiftVideoHeight / 100;
        // this.renderer2.setStyle(zwiftVideoElement, 'top', `-${topPaddingFromHeightCurrentVideo}px`);
        // this.renderer2.setStyle(this.hostElementRef.nativeElement, 'height', `${newHeightOfVideoWrapper}px`);

        const scale = Math.floor(Math.max(host.width / area.width, host.height / area.height) * 10) / 10;
        // for alight bottom right
        const addedGap = 4;
        const xOffset = Math.max(0, Math.floor(host.width - area.width * scale)) - addedGap;
        const yOffset = Math.max(0, Math.floor(host.height - area.height * scale)) - addedGap;
        const transformValue = `translate(-50%, -50%) scale(${scale}, ${scale}) translate(50%, 50%)`
          + ` translate(${-area.x + xOffset}px, ${-area.y + yOffset}px)`;
        console.log(scale, host, area, xOffset, yOffset, transformValue);
        this.renderer2.setStyle(zwiftVideoElement, 'transform', transformValue);

        const clipX1 = area.x;
        const clipY1 = area.y;
        const clipX2 = area.x + area.width;
        const clipY2 = area.y + area.height;
        this.renderer2.setStyle(zwiftVideoElement,
          'clip-path',
          `polygon(${clipX1}px ${clipY1}px, ${clipX2}px ${clipY1}px, ${clipX2}px ${clipY2}px, ${clipX1}px ${clipY2}px)`);
        this.renderer2.setStyle(zwiftVideoElement, 'background-color', 'red');

        // console.log(`Update style ${transformValue}`);
        // this.changeDetectorRef.markForCheck();
      });

    timer(3000, 30000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const area = this.getPrivateStatsArea();
        this.recognizedArea$.next(area);
        console.log(area);
      });
  }

  private getPrivateStatsArea(): Dimensions | null {
    const ctx = this.canvas.nativeElement.getContext('2d');
    // {
    //       willReadFrequently: true,
    //     }
    const video = this.zwiftVideo.nativeElement;
    const expectedColors = ['#1192CB'/*, '#FDF7FC'*//*, '#0A0405'*/];
    const widthPart = 0.2;
    const heightPart = 0.3;

    const startTime = new Date().getTime();
    const partW = this.canvas.nativeElement.width = Math.floor(video.videoWidth * widthPart);
    const partH = this.canvas.nativeElement.height = Math.floor(video.videoHeight * heightPart);
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      // ctx.fillStyle = 'orange';
      // ctx.strokeStyle = 'orange';
      // ctx.beginPath();

      // const bitmap = ctx.getImageData(0, 0, partW, partH);

      let minX = 99999;
      let maxX = -1;
      let minY = 99999;
      let maxY = -1;
      let totalColorMatches = 0;

      interface StatsMap {
        [name: number]: number;
      }
      const successX:  StatsMap = {};
      const successY:  StatsMap = {};
      const attempts = 100;
      for (let x = 0; x < attempts; x++) {
        const ix = 2 + Math.floor(partW * x / attempts);
        for (let y = 0; y < attempts; y++) {
          const iy = 30 + Math.floor(partH * y / attempts);
          const pixel = ctx.getImageData(ix, iy, 1, 1).data;
          const [r, g, b, a] = pixel;
          if (expectedColors.findIndex(e => this.arePixelsSimilar(e,[r, g, b])) > -1) {
            totalColorMatches++;
            // minX = Math.min(minX, ix);
            // maxX = Math.max(maxX, ix);
            // minY = Math.min(minY, iy);
            // maxY = Math.max(maxY, iy);
            // ctx.fillStyle = ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
            // ctx.beginPath();
            // ctx.fillRect(ix - 2, iy - 2, 3, 3);
            // ctx.closePath();
            // console.log(`Filled pixel`, pixel);
            successX[ix] = (successX[ix] || 0) + 1;
            successY[iy] = (successY[iy] || 0) + 1;
          } else {
            // failedX[ix] = (failedX[ix] || 0) + 1;
            // failedY[iy] = (failedY[iy] || 0) + 1;
          }
        }
      }

      // console.log('successX', successX);
      // console.log('successY', successY);
      const positiveMatch = 0.36;
      Object.entries(successX).forEach(([ixStr, matched]) => {
        if (matched / attempts >= positiveMatch) {
          const ix = parseFloat(ixStr);
          minX = Math.min(minX, ix);
          maxX = Math.max(maxX, ix);
        }
      });
      Object.entries(successY).forEach(([iyStr, matched]) => {
        if (matched / attempts >= positiveMatch) {
          const iy = parseFloat(iyStr);
          minY = Math.min(minY, iy);
          maxY = Math.max(maxY, iy);
        }
      });

      // ctx.closePath();
      console.log(`Time spent on Zwift detect ${new Date().getTime() - startTime} ms.`);

      if (maxX === -1 || maxY === -1) {
        console.log(`No panel detected fallback to part of screen.`);
        return null;
      }

      const addedGap = 4;
      const result = {
        x: Math.max(minX - addedGap, 0),
        y: Math.max(minY - addedGap, 0),
        width: maxX - minX + addedGap * 2,
        height: maxY - minY + addedGap * 2,
      };
      // console.log(`Detected matches:${totalColorMatches} from:${partW}x${partH} ${result}`);
      return result;
    }

    return null;
  }

  private arePixelsSimilar(pixel1: string, pixel2: [number, number, number]): boolean {
    // Convert the first pixel from string format to RGB array
    const rgb1 = [
      parseInt(pixel1.substr(1, 2), 16),
      parseInt(pixel1.substr(3, 2), 16),
      parseInt(pixel1.substr(5, 2), 16)
    ];

    // Calculate the percentage difference between the RGB values
    const diffR = Math.abs(rgb1[0] - pixel2[0]);
    const diffG = Math.abs(rgb1[1] - pixel2[1]);
    const diffB = Math.abs(rgb1[2] - pixel2[2]);

    const similarityR = (1 - diffR / 255) * 100;
    const similarityG = (1 - diffG / 255) * 100;
    const similarityB = (1 - diffB / 255) * 100;
    //6641
    // Check if any of the color channels have at least 5% similarity
    const similarity = 93;
    const result = similarityR >= similarity && similarityG >= similarity && similarityB >= similarity;
    if (false) {
      console.log(``);
      console.log(`result ${result.toString().toUpperCase()}`);
      console.log(`pixel1 ${rgb1[0]} ${rgb1[1]} ${rgb1[2]}`);
      console.log(`pixel2 ${pixel2[0]} ${pixel2[1]} ${pixel2[2]}`);
      // console.log(`diffR:${diffR}, similarityR:${similarityR}`);
      console.log(`similarityR:${similarityR}, similarityG:${similarityG}, similarityB:${similarityB}`);
    }
    return result;
  }

  private handleHostRefWidthChanges(): void {
    this.hostResizeObserver = new ResizeObserver(entries => {
      this.ngZone.run(() => {
        const rect = entries[0].contentRect;

        this.hostWidth$.next({
          ...emptyRect,
          width: rect.width,
          height: rect.height,
        });
        console.log(`handleHostRefWidthChanges ${this.hostWidth$.getValue()}`, rect);
      });
    });

    this.hostResizeObserver.observe(this.hostElementRef.nativeElement);
  }

  private handleVideoDimensionsChanges(): void {
    this.videoResizeObserver = new ResizeObserver(entries => {
      this.ngZone.run(() => {
        const { width, height } = entries[0].contentRect;

        this.videoDimensions$.next({
          ...emptyRect,
          width,
          height,
        });
        console.log(`handleVideoDimensionsChanges`, this.videoDimensions$.getValue());
      });
    });

    this.videoResizeObserver.observe(this.zwiftVideo.nativeElement);
  }

  ngOnDestroy() {
    this.hostResizeObserver?.unobserve(this.hostElementRef.nativeElement);
    this.videoResizeObserver?.unobserve(this.zwiftVideo.nativeElement);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
